import { RadioGroup } from '@chakra-ui/react'
import * as React from 'react'

import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { Grid } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'

import { InputRadio } from './InputRadio'

type TRadioInputProps = Pick<
  PropsOf<typeof InputRadio>,
  'bgColor' | 'selectedBgColor' | 'isDisabled' | 'minHeight' | 'radioMarginTop'
>

type TProps<TOption> = NoChildren &
  TRadioInputProps & {
    /**
     * if true, render bottom border line under each item
     */
    hasDivider?: PropsOf<typeof InputRadio>['hasDivider']
    /**
     * name of the group of the radio inputs
     */
    groupName?: string
    /**
     * array of options that the radio buttons should be rendered for
     */
    options: RoA<TOption>
    /**
     * prop of the option that will return string/number id, unique amongst the array
     * used as a key AND for comparing with selected option
     */
    idPropKey: keyof TOption
    /**
     * option from the options array, that is currently selected
     */
    selectedOption: Nullable<TOption>
    /**
     * callback on option click
     */
    onSelect: (option: TOption) => void
    /**
     * vertical space between individual items
     */
    spacing?: PropsOf<typeof Stack>['spacing']
    /**
     * option label renderer
     */
    renderOptionLabel: (option: TOption) => React.ReactNode
    /**
     * button to uncheck the selected radio button group, placed inside the grid together with the radio button items
     */
    cancelButton?: React.ReactNode
  }

// with no value set on the first render, the radio group would appear uncontrolled https://github.com/chakra-ui/chakra-ui/issues/2451
// and would not work once some value is provided – provide some stupid default when nothing is selected yet
const FAKE_EMPTY_VALUE = `NO_VALUE_SELECTED`

export const InputRadioGroup = <TOption,>({
  hasDivider,
  options,
  onSelect,
  idPropKey,
  groupName,
  selectedOption,
  renderOptionLabel,
  spacing = `medium`,
  cancelButton = null,
  ...radioInputProps
}: TProps<TOption>) => {
  const onOptionChange = React.useCallback(
    (optionId: ID) => {
      const selectedOption = options.find((option) => String(option[idPropKey]) === optionId)

      if (!selectedOption) {
        return
      }

      onSelect(selectedOption)
    },
    [idPropKey, onSelect, options],
  )

  return (
    <RadioGroup
      name={groupName}
      onChange={onOptionChange}
      value={selectedOption ? String(selectedOption[idPropKey]) : FAKE_EMPTY_VALUE}
    >
      <Grid
        gridTemplateColumns={`repeat(auto-fill, minmax(300px, 1fr))`}
        columnGap={`large`}
        rowGap={spacing}
      >
        {options.map((option) => (
          <InputRadio
            {...radioInputProps}
            key={String(option[idPropKey])}
            value={String(option[idPropKey])}
            hasDivider={hasDivider}
            dividerSpacing={spacing}
            isChecked={option[idPropKey] === selectedOption?.[idPropKey]}
          >
            {renderOptionLabel(option)}
          </InputRadio>
        ))}
        <AnimatedHeight isOpen={Boolean(cancelButton)}>{cancelButton}</AnimatedHeight>
      </Grid>
    </RadioGroup>
  )
}
